import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import type { AssetTypePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Pencil, Trash2 } from "lucide-react";

const categoryLabels: Record<string, string> = {
  barrier: "Speed Barrier",
  dugout: "Dugout",
  pavilion: "Pavilion",
  jersey: "Jersey",
  other: "Other",
};

export default function AssetTypeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [type, setType] = useState<AssetTypePublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .getAssetType(id)
      .then((res) => setType(res.asset_type))
      .catch((e) =>
        setError(e instanceof api.ApiError ? e.message : "Failed to load asset type"),
      )
      .finally(() => setLoading(false));
  }, [id]);

  async function handleDelete() {
    if (!id || !confirm("Delete this asset type? This action cannot be undone.")) return;
    try {
      await api.deleteAssetType(id);
      navigate("/asset-types");
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to delete asset type");
    }
  }

  if (loading) return <p className="text-muted-foreground" data-page="asset-type-detail" data-state="loading">Loading...</p>;
  if (error && !type) return <p className="text-sm text-destructive" data-page="asset-type-detail" data-state="error" data-error>{error}</p>;
  if (!type) return <p className="text-muted-foreground" data-page="asset-type-detail" data-state="empty">Asset type not found</p>;

  return (
    <div className="flex flex-col gap-6" data-page="asset-type-detail" data-state={error ? "error" : "ready"}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-region="header">
        <div>
          <h1 className="text-2xl font-semibold">{type.name}</h1>
          <p className="text-muted-foreground">
            {categoryLabels[type.category] ?? type.category} -- {type.width_mm} x {type.height_mm} mm
          </p>
        </div>
        <div className="flex gap-2">
          {type._actions.can_edit && (
            <Link
              to={`/asset-types/${type.id}/edit`}
              className={buttonVariants({ variant: "outline" })}
              data-action="edit"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Link>
          )}
          {type._actions.can_delete && (
            <Button variant="destructive" onClick={handleDelete} data-action="delete">
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Details</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Name</dt>
              <dd className="mt-1">{type.name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Category</dt>
              <dd className="mt-1">{categoryLabels[type.category] ?? type.category}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Dimensions (mm)</dt>
              <dd className="mt-1">
                {type.width_mm} x {type.height_mm}
                {type.depth_mm != null ? ` x ${type.depth_mm}` : ""}
              </dd>
            </div>
            {type.description && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Description</dt>
                <dd className="mt-1">{type.description}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Assets</dt>
              <dd className="mt-1">{type.asset_count}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {type.ad_spaces && type.ad_spaces.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Template Ad Spaces</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Size (mm)</TableHead>
                    <TableHead>Offset (mm)</TableHead>
                    <TableHead>Sort Order</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {type.ad_spaces.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {s.width_mm} x {s.height_mm}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {s.offset_x_mm}, {s.offset_y_mm}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {s.sort_order}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
